import { Link, useLocation, useNavigate } from "react-router-dom";
import { Calculator, BarChart3, History, ArrowLeft, LogOut } from "lucide-react";
import { formatINR } from "@/lib/pos";
import { useAuth } from "@/context/AuthContext";

const TABS = [
  { to: "/admin/pos", label: "Calculator", icon: Calculator, testid: "pos-tab-calculator" },
  { to: "/admin/pos/dashboard", label: "Sales", icon: BarChart3, testid: "pos-tab-dashboard" },
  { to: "/admin/pos/history", label: "History", icon: History, testid: "pos-tab-history" },
];

export default function PosShell({ children, todayTotal = null }) {
  const { pathname } = useLocation();
  const nav = useNavigate();
  const { logout } = useAuth();

  return (
    <div data-testid="pos-shell" className="min-h-screen bg-slate-100 font-pos flex flex-col">
      <header className="sticky top-0 z-40 bg-slate-900 text-white shadow-lg">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              data-testid="pos-back-button"
              onClick={() => nav("/admin")}
              className="w-9 h-9 rounded-[4px] bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              aria-label="Back to admin"
            >
              <ArrowLeft size={18} />
            </button>
            <div className="min-w-0">
              <div className="font-black text-lg leading-none tracking-tight truncate">
                Stationery Pro
              </div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#7cc7ee] font-bold mt-1">
                Counter POS
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div
              data-testid="pos-header-today-total"
              className="text-right px-3 py-1.5 rounded-[4px] bg-white/10"
            >
              <div className="text-[10px] uppercase tracking-[0.16em] text-white/60 font-bold">
                Today
              </div>
              <div className="font-black text-xl leading-none tabular-nums">
                {todayTotal == null ? (
                  <span className="inline-block w-14 h-4 rounded bg-white/25 animate-pulse align-middle" />
                ) : (
                  formatINR(todayTotal)
                )}
              </div>
            </div>
            <button
              type="button"
              data-testid="pos-logout-button"
              onClick={() => {
                logout();
                nav("/admin/login", { replace: true });
              }}
              className="hidden sm:flex w-9 h-9 rounded-[4px] bg-white/10 hover:bg-white/20 items-center justify-center transition-colors"
              aria-label="Logout"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>

        <nav className="hidden sm:block border-t border-white/10">
          <div className="mx-auto max-w-5xl px-6 flex">
            {TABS.map((t) => {
              const active = pathname === t.to;
              return (
                <Link
                  key={t.to}
                  to={t.to}
                  data-testid={`${t.testid}-desktop`}
                  className={`flex items-center gap-2 px-5 py-3 text-sm font-bold border-b-[3px] transition-colors ${
                    active
                      ? "border-[#299fe0] text-white"
                      : "border-transparent text-white/55 hover:text-white"
                  }`}
                >
                  <t.icon size={16} />
                  {t.label}
                </Link>
              );
            })}
          </div>
        </nav>
      </header>

      <main className="flex-1 pb-24 sm:pb-10">{children}</main>

      <nav
        data-testid="pos-bottom-nav"
        className="sm:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t-2 border-slate-200"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="grid grid-cols-3">
          {TABS.map((t) => {
            const active = pathname === t.to;
            return (
              <Link
                key={t.to}
                to={t.to}
                data-testid={t.testid}
                className={`flex flex-col items-center justify-center gap-1 py-3 font-bold text-[11px] uppercase tracking-[0.1em] transition-colors ${
                  active ? "text-[#0e6ea6] bg-[#299fe0]/10" : "text-slate-500"
                }`}
              >
                <t.icon size={22} strokeWidth={active ? 2.6 : 2} />
                {t.label}
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
